import { createContext, useContext } from 'react'

import type { Company } from '@/gen/inventory/v1/company_service_pb'
import type { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { companyLifecycleLabel, ongoingAssessmentStatusLabel, riskLevelLabel } from '@/const/label'

import type { EventProperties } from '@/lib/analytics/events'

export const ASSESSMENT_EXPORT_SOURCES = {
  FINISH_ASSESSMENT_MODAL: 'finish_assessment_modal',
  PAST_ASSESSMENT: 'past_assessment',
} as const

export type AssessmentAnalyticsContext = EventProperties & {
  assessmentId: string
  companyId: string
  companyName: string
  assessmentStatus?: string
  vendorLifecycle?: string
}

export type AssessmentAnalyticsContextWithProject = AssessmentAnalyticsContext & {
  projectId?: string
  projectName?: string
}

type BuildAssessmentAnalyticsContextParams = {
  ongoingAssessment: OngoingAssessment
  company?: Company
}

export const buildAssessmentAnalyticsContext = ({
  ongoingAssessment,
  company,
}: BuildAssessmentAnalyticsContextParams): AssessmentAnalyticsContextWithProject => {
  return {
    assessmentId: ongoingAssessment.id,
    companyId: ongoingAssessment.orgCompanyId,
    companyName: ongoingAssessment.orgCompanyName || company?.profile?.name || '',
    assessmentStatus: ongoingAssessmentStatusLabel[ongoingAssessment.status],
    vendorLifecycle: company ? companyLifecycleLabel[company.lifecycle] : undefined,
    projectId: ongoingAssessment.projectId || undefined,
    projectName: ongoingAssessment.projectName || undefined,
  }
}

export const getInherentRiskTrackingValue = (riskLevel: RiskLevel | undefined) => {
  if (riskLevel == null) {
    return undefined
  }

  return riskLevelLabel[riskLevel]
}

export const countWords = (value: string) => {
  const normalized = value.trim()
  if (!normalized) {
    return 0
  }

  return normalized.split(/\s+/).length
}

const AssessmentAnalyticsCtx = createContext<AssessmentAnalyticsContextWithProject | undefined>(
  undefined,
)

export const AssessmentAnalyticsProvider = AssessmentAnalyticsCtx.Provider

export const useAssessmentAnalytics = () => {
  return useContext(AssessmentAnalyticsCtx)
}
